import React, { useMemo } from "react";
import styled, { css, useTheme } from "styled-components";
import GenericPlot from "views/components/visualizations/GenericPlot";
import AppConfig from "util/AppConfig";
import {
  getHoverTemplateSettings,
  getFormatSettingsByData
} from "views/components/visualizations/utils/chartLayoutGenerators";
import FieldUnit from "views/logic/aggregationbuilder/FieldUnit";
const GraphWrapper = styled.div(
  ({ $width }) => css`
    height: 200px;
    width: ${$width}px;
  `
);
const getMaxDailyValue = (arr) => arr.reduce((a, b) => Math.max(a, b), 0);
const TrafficGraph = ({ width, traffic, trafficLimit = void 0 }) => {
  const theme = useTheme();
  const isCloud = AppConfig.isCloud();
  const yValues = useMemo(() => Object.values(traffic), [traffic]);
  const chartData = useMemo(
    () => [
      {
        type: "bar",
        x: Object.keys(traffic),
        y: yValues,
        ...getHoverTemplateSettings({
          convertedValues: yValues,
          unit: FieldUnit.fromJSON({ abbrev: "b", unit_type: "binary_size" })
        })
      }
    ],
    [traffic, yValues]
  );
  const maxDailyValue = useMemo(() => getMaxDailyValue(yValues), [yValues]);
  const trafficLimitAnnotation = useMemo(
    () => ({
      annotations: [
        {
          showarrow: false,
          text: "<b>Licensed traffic limit</b>",
          align: "right",
          x: 1,
          xref: "paper",
          xanchor: "right",
          y: trafficLimit,
          yanchor: "bottom",
          font: {
            color: theme.colors.variant.danger
          }
        }
      ]
    }),
    [theme.colors.variant.danger, trafficLimit]
  );
  const trafficLimitAnnotationShape = useMemo(
    () => ({
      shapes: [
        {
          type: "line",
          x0: 0,
          x1: 1,
          y0: trafficLimit,
          y1: trafficLimit,
          name: "Traffic Limit",
          xref: "paper",
          yref: "y",
          line: {
            color: theme.colors.variant.danger
          }
        }
      ]
    }),
    [theme.colors.variant.danger, trafficLimit]
  );
  const valuesToGetFormatSettings = useMemo(
    () => trafficLimit ? [...yValues, trafficLimit] : yValues,
    [trafficLimit, yValues]
  );
  const notZoomedLayout = useMemo(
    () => ({
      rangemode: "tozero",
      ...getFormatSettingsByData("binary_size", valuesToGetFormatSettings)
    }),
    [valuesToGetFormatSettings]
  );
  const zoomedLayout = useMemo(
    () => ({
      rangemode: "tozero",
      ...getFormatSettingsByData("binary_size", yValues)
    }),
    [yValues]
  );
  const layout = useMemo(
    () => ({
      showlegend: false,
      margin: {
        l: 60
      },
      xaxis: {
        type: "date",
        title: {
          text: "Time shown in UTC"
        }
      },
      hovermode: "x",
      hoverlabel: {
        namelength: -1
      },
      yaxis: notZoomedLayout,
      updatemenus: [
        {
          buttons: [
            {
              // args: ['yaxis.range', [0, isCloud ? trafficLimit : range]],
              args: [
                {
                  "yaxis.range": zoomedLayout.range,
                  "yaxis.tickvals": zoomedLayout.tickvals,
                  "yaxis.ticktext": zoomedLayout.ticktext
                }
              ],
              args2: [
                {
                  "yaxis.range": notZoomedLayout.range,
                  "yaxis.tickvals": notZoomedLayout.tickvals,
                  "yaxis.ticktext": notZoomedLayout.ticktext
                }
              ],
              label: "Zoom/Reset",
              method: "relayout"
            }
          ],
          direction: "right",
          showactive: false,
          bordercolor: theme.colors.global.contentBackground,
          font: {
            color: theme.colors.global.link
          },
          active: 1,
          type: "buttons",
          visible: trafficLimit && maxDailyValue < trafficLimit && !isCloud,
          xanchor: "right",
          yanchor: "top",
          x: 1,
          y: 1.3
        }
      ]
    }),
    [
      isCloud,
      notZoomedLayout,
      maxDailyValue,
      theme.colors.global.contentBackground,
      theme.colors.global.link,
      trafficLimit,
      zoomedLayout.range,
      zoomedLayout.ticktext,
      zoomedLayout.tickvals
    ]
  );
  const trafficLayout = useMemo(() => {
    const layoutWithTrafficLimit = { ...layout, ...trafficLimitAnnotation, ...trafficLimitAnnotationShape };
    return trafficLimit ? layoutWithTrafficLimit : layout;
  }, [layout, trafficLimit, trafficLimitAnnotation, trafficLimitAnnotationShape]);
  return /* @__PURE__ */ React.createElement(GraphWrapper, { $width: width }, /* @__PURE__ */ React.createElement(GenericPlot, { chartData, layout: trafficLayout }));
};
export default TrafficGraph;
